import * as React from 'react';
import { translate } from 'components/translate-hoc';

import Select, { CSSObjectWithLabel, StylesConfig } from 'react-select';

import { ITranslationProps } from 'types/translation';
import { getModeClass } from 'utils/css';

import { IDropdownField } from './fields.types';

type IDropdownFieldProps = IDropdownField & ITranslationProps;

interface IOption {
    value: string;
    label: string;
}

const getCustomStyles = (shouldInheritStyles: boolean): StylesConfig => ({
    singleValue: (provided: CSSObjectWithLabel) => ({
        ...provided,
        color: shouldInheritStyles ? 'inherit' : 'var(--content-text-color)'
    }),
    menu: (provided: CSSObjectWithLabel) => ({
        ...provided,
        background: shouldInheritStyles ? '#1a1a1a' : 'var(--subcontent-background-color)',
        border: '1px solid #606060'
    }),
    control: (provided: CSSObjectWithLabel) => ({
        ...provided,
        backgroundColor: 'transparent',
        borderRadius: 0,
        boxShadow: 'none',
        color: shouldInheritStyles ? 'inherit' : 'var(--content-text-color)',
        borderColor: '#606060',
        ':hover': {
            ...provided[':hover'],
            borderColor: '#606060'
        }
    }),
    option: (provided: CSSObjectWithLabel, state) => ({
        ...provided,
        color: shouldInheritStyles ? '#fff' : 'var(--content-text-color)',
        backgroundColor: state.isSelected
            ? '#606060'
            : shouldInheritStyles ? '#1a1a1a' : 'var(--subcontent-background-color)',
        ':hover': {
            ...provided[':hover'],
            backgroundColor: '#606060'
        }
    })
});

export class DropdownField extends React.PureComponent<IDropdownFieldProps> {
    public render(): JSX.Element {
        const {
            shouldInheritStyles,
            meta,
            dangerouslyTranslateWithHTML: translateKey,
            translatePrefix
        } = this.props;

        const dropdownClassName = getModeClass('sitegen-dropdown', shouldInheritStyles);
        const labelText: string = translateKey(this.getLabelKey());
        const isWarningVisible = meta.error && meta.submitFailed;
        const errorMessage = isWarningVisible ? translateKey(`${translatePrefix}${meta.error.message}`) : '';
        const className: string = isWarningVisible
            ? `${dropdownClassName}__select ${dropdownClassName}__select_has-warnings`
            : `${dropdownClassName}__select`;

        return (
            <div className={dropdownClassName}>
                <div className={`${dropdownClassName}__label`}>{labelText}</div>
                <div className={`${dropdownClassName}__container`}>
                    <Select
                        placeholder=""
                        closeMenuOnSelect
                        options={this.getViewOptions()}
                        onChange={this.setFieldInput}
                        classNamePrefix="sitegen-dropdown"
                        className={className}
                        styles={getCustomStyles(shouldInheritStyles)}
                    />
                    <div className={`${dropdownClassName}__error`}>{errorMessage}</div>
                </div>
            </div>
        );
    }

    private setFieldInput = (option: IOption): void => {
        // @ts-ignore
        // TODO: remove ignore once issue with typings is resolved:
        // https://github.com/final-form/react-final-form/issues/441
        this.props.input.onChange(option.value);
    };

    private getLabelKey(): string {
        const { input, translatePrefix } = this.props;
        return `${translatePrefix}${input.name}`;
    }

    private getViewOptions(): IOption[] {
        const {
            fieldValue,
            optionTranslatePrefix,
            dangerouslyTranslateWithHTML: translateKey
        } = this.props;
        return fieldValue.map(
            (item: string, index: number): IOption => {
                const localizedItem = translateKey(
                    `${this.getLabelKey()} ${optionTranslatePrefix}${index + 1}`,
                    { defaultValue: item }
                );
                return { value: item, label: localizedItem };
            }
        );
    }
}

export default translate()(DropdownField) as typeof DropdownField;
